import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { calculatePercentage, formatDataGeneral, getAttributeValue, getFileLinkIfExist, isDateInSelectedYear } from "utils/utils";
import {
    documentHubLinksComplete,
    isEvidenceLinkUploaded,
    renderDpatEvidenceLink,
    scoreOnlyIfAllEvidenceComplete,
} from "utils/renderDpatEvidenceLink";
import { documentsColumn } from "utils/tableColums";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const MONITORING_DOCUMENT_TYPES = [
    "Monitoring, Building and Inspection  Reports",
    "Monitoring Checklist and Report",
];

function fileUrl(eventId, dataElementUid) {
    return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function buildMonitoringDocumentLinks(documents) {
    const documentData = documents?.data;
    const documentReports = documents?.reports;

    return MONITORING_DOCUMENT_TYPES.map((title) => {
        const matchedDocs = formatDataGeneral(
            documentData,
            "DPAT | M&E Tool Type",
            title,
        ) || [];

        let documentLink = "";
        if (matchedDocs.length > 0) {
            documentLink = getFileLinkIfExist(
                documentReports,
                "MjPmcbueEdU",
                matchedDocs[0]?.trackedEntity,
            );
        }

        return {
            title,
            link: documentLink
                ? fileUrl(documentLink, "MjPmcbueEdU")
                : "Not Uploaded",
        };
    });
}

function computeScoreii(monitoringRows, documentLinks) {
    const hasEnoughMonitoring =
        monitoringRows.length > 1 &&
        monitoringRows.every((entry) => entry.reportAvailability === "YES");

    return hasEnoughMonitoring && documentHubLinksComplete(documentLinks) ? 2 : 0;
}

function getYearFromDateValue(value) {
    if (value == null || value === "" || value === "N/A") return null;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.getFullYear();
    const match = String(value).match(/\b(19|20)\d{2}\b/);
    return match ? Number(match[0]) : null;
}

function isMonitoringDateInYear(dateValue, selectedYear) {
    return isDateInSelectedYear(dateValue, selectedYear);
}

const MonitoringProjectAndActivity = forwardRef(({
    year,
    district,
    documents,
    hideComment,
    savedAssessmentData,
    assessmentStartLoading,
    isAssessmentStarted,
}, ref) => {

    const { Header, Content } = Layout;
    const { Title, Text } = Typography;
    const resolvedYear =
        year != null && typeof year === "object" && !Array.isArray(year)
            ? year.value ?? year.label
            : year;
    const districtId =
        typeof district === "string"
            ? district
            : district?.value?.id ?? district?.id ?? "";
    const [data, setData] = useState([]);
    const [monitoring, setMonitoring] = useState([]);
    const [documentLinks, setDocumentLinks] = useState([]);
    const [scorei, setScorei] = useState(0);
    const [scoreii, setScoreii] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const [maxScore, setMaxScore] = useState(4);

    useImperativeHandle(ref, () => ({
        getData: () => ({
            indicator: "PI1",
            area: "Annual Action Plan Implementation",
            maxScore,
            data,
            monitoring,
            links: documentLinks,
            scorei,
            scoreii,
            percentage
        }),
    }));

    const activityColumns = [
        {
            title: "Total Budgetary Allocation for planned M&E activities",
            dataIndex: "aapApproved",
            key: "aapApproved"
        },
        {
            title: "Amount released for planned M&E activities",
            dataIndex: "aapImplented",
            key: "aapImplented"
        },
        {
            title: "% Budgetary Allocation released for planned M&E activities",
            dataIndex: "percentage",
            key: "percentage"
        },
        {
            title: "Report",
            dataIndex: "report",
            key: "report",
            render: (value) => renderDpatEvidenceLink(value, "View Report"),
        }
    ];

    const monitoringColumns = [
        {
            title: "Date of Monitoring",
            dataIndex: "date",
            key: "date"
        },
        {
            title: "Projects monitored",
            dataIndex: "monitored",
            key: "monitored"
        },
        {
            title: "Stakeholders involved",
            dataIndex: "involved",
            key: "involved"
        },
        {
            title: "Report Available (YES/NO)",
            dataIndex: "reportAvailability",
            key: "reportAvailability"
        }
    ];

    const DPAT_SLUG = "pi1.0-1.2";

    const documentTableColumns = useMemo(
        () => documentsColumn.map((col) =>
            col.dataIndex === "link"
                ? {
                    ...col,
                    render: (value) => renderDpatEvidenceLink(value, "View Document"),
                }
                : col
        ),
        [],
    );

    function getData() {
        axios
            .get(`/tracker/trackedEntities?orgUnit=${district}&program=YHVtzXj8iIC&startDate=${year}-01-01&endDate=${year}-12-31`)
            .then(result => {
                if (result.data.instances.length > 0) {

                    axios
                        .get(`/tracker/events?program=YHVtzXj8iIC&orgUnit=${district}&startDate=${year}-01-01&endDate=${year}-12-31`)
                        .then(resp => {
                            const budgets = result.data.instances;
                            const reports = resp.data.instances;
                            let amountReleased = 0;
                            let allocatedAmount = 0;

                            const monitoringBudgets = formatDataGeneral(budgets, "Budget Category", "M&E Activities") || [];
                            let reportUrl = "Not Uploaded";

                            monitoringBudgets.forEach((budget) => {
                                const currentReport = reports.find(rep => rep.trackedEntity === budget.trackedEntity);
                                const reportLink = getFileLinkIfExist(reports, "hM6AUNKRbKB", budget.trackedEntity);

                                if (reportLink) {
                                    reportUrl = fileUrl(reportLink, "hM6AUNKRbKB");
                                }
                                allocatedAmount = getAttributeValue("Allocated Budget", budget);
                                if (currentReport) {
                                    currentReport.dataValues.forEach(rep => {

                                        if (rep.dataElement === "MPLHBtSdEyn") {
                                            amountReleased = rep.value;
                                        }
                                    });
                                }

                            });

                            const percentageNum = Number(
                                calculatePercentage(amountReleased, allocatedAmount).toFixed(2),
                            );
                            setPercentage(percentageNum);

                            const temp = {
                                aapApproved: allocatedAmount,
                                aapImplented: amountReleased,
                                percentage: percentageNum,
                                report: reportUrl,
                            };

                            setData([temp]);

                        })
                        .catch(err => console.log(err))
                }

            })
            .catch(err => console.log(err))
    }

    function getInspectorateMonitoring() {
        if (!districtId || resolvedYear == null) return;

        axios
            .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=p1ccS2ROn0Q&pageSize=5000`)
            .then(result => {
                if (result.data.instances.length > 0) {

                    axios
                        .get(`/tracker/events?program=p1ccS2ROn0Q&orgUnit=${districtId}&pageSize=5000`)
                        .then(resp => {
                            const monitorings = result.data.instances;
                            const reports = resp.data.instances;
                            const temp = [];

                            monitorings.forEach((project, index) => {
                                const monitoringDate = getAttributeValue("Date", project);
                                if (!isMonitoringDateInYear(monitoringDate, resolvedYear)) {
                                    return;
                                }

                                const reportLink = getFileLinkIfExist(
                                    reports,
                                    "hM6AUNKRbKB",
                                    project.trackedEntity,
                                );
                                const reportAvailability = reportLink ? "YES" : "NO";

                                temp.push({
                                    key: project.trackedEntity || index + 1,
                                    date: monitoringDate !== "N/A" ? monitoringDate : "Not Provided",
                                    monitored: getAttributeValue("Inspection Details", project),
                                    involved: getAttributeValue("DPAT | Stakeholders Involved", project),
                                    reportAvailability,
                                });
                            });

                            setMonitoring(temp);

                        })
                        .catch(err => console.log(err))
                } else {
                    setMonitoring([]);
                }

            })
            .catch(err => console.log(err))
    }

    const loadFromDhis2 = useCallback(() => {
        setDocumentLinks(buildMonitoringDocumentLinks(documents));
        getData();
        getInspectorateMonitoring();
    }, [districtId, resolvedYear, documents]);

    useDpatIndicatorDataSource({
        isAssessmentStarted,
        savedAssessmentData,
        assessmentStartLoading,
        loadFromDhis2,
        applySnapshot: (snap) => {
            if (snap.data) setData(snap.data);
            if (snap.monitoring) setMonitoring(snap.monitoring);
            if (snap.links) setDocumentLinks(snap.links);
            if (snap.scorei != null) setScorei(snap.scorei);
            if (snap.scoreii != null) setScoreii(snap.scoreii);
            if (snap.percentage != null) setPercentage(snap.percentage);
            if (snap.maxScore != null) setMaxScore(snap.maxScore);
        },
        deps: [year, district, documents, loadFromDhis2],
    });

    useEffect(() => {
        if (isAssessmentStarted) return;
        setScoreii(computeScoreii(monitoring, documentLinks));
    }, [monitoring, documentLinks, isAssessmentStarted]);

    useEffect(() => {
        if (isAssessmentStarted) return;
        const budgetRow = data[0];
        const pct = budgetRow?.percentage ?? 0;
        const rawScore = pct >= 100 ? 2 : 0;
        setScorei(
            scoreOnlyIfAllEvidenceComplete({
                rows: data,
                rowLinkKeys: ["report"],
                score: rawScore,
                minRows: 1,
            }),
        );
    }, [data, isAssessmentStarted]);

    const uploadedDocumentCount = documentLinks.filter(
        (doc) => isEvidenceLinkUploaded(doc.link),
    ).length;
    const monitoringWithReports = monitoring.filter(
        (entry) => entry.reportAvailability === "YES",
    ).length;

    return (
        <Comment
            data={data}
            year={year}
            districtId={district}
            tableCommentedId={`pi1.0-1.2-${year}`}
            hideComment={hideComment}
        >
            {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
                    <DpatIndicatorCmsIntro
                        year={year}
                        slug={DPAT_SLUG}
                        mainTitleStyle={{ marginTop: "20px" }}
                    />

                    <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                              <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.2i Actual Score:" score={scorei} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.2ii Actual Score:" score={scoreii} />
                    </IndicatorScoreSection>
                    <Title level={4} style={{ marginTop: "20px" }}>
                        I- Evidence of clear budgetary release for M&E activities
                    </Title>
                    <Table
                        columns={activityColumns}
                        dataSource={data}
                        pagination={false}
                        bordered
                    />

                    <Title level={4} style={{ marginTop: "20px" }}>
                        II- Evidence of multi-stakeholder participation in monitoring
                    </Title>
                    <Table
                        columns={monitoringColumns}
                        dataSource={monitoring}
                        pagination={false}
                        bordered
                        rowKey="key"
                    />

                    <Title level={4} style={{ marginTop: "20px" }}>
                        III- Document Evidence
                    </Title>
                    <Table
                        columns={documentTableColumns}
                        dataSource={documentLinks}
                        pagination={false}
                        bordered
                        rowKey="title"
                    />

                    <Title level={5} style={{ marginTop: "30px" }}>Conclusion</Title>
                    <Content>
                        <p>
                            PI 1.0-1.2i: {percentage}% of budgetary allocation was released for the
                            implementation of planned M&amp;E monitoring activities in {year}.
                        </p>
                        <p>
                            PI 1.0-1.2ii:{" "}
                            {monitoring.length > 1 ? (
                                <>
                                    The district recorded {monitoring.length} multi-stakeholder
                                    monitoring participation entries in {year}, with reports
                                    available for {monitoringWithReports} of them.
                                    {scoreii > 0 ? (
                                        <>
                                            {" "}
                                            All monitoring entries have report attachments and the
                                            required document evidence has been uploaded, so the
                                            indicator scores.
                                        </>
                                    ) : (
                                        <>
                                            {" "}
                                            The indicator does not score because{" "}
                                            {monitoringWithReports < monitoring.length
                                                ? "not all monitoring entries have report attachments"
                                                : uploadedDocumentCount < documentLinks.length
                                                    ? "required monitoring documents are not fully uploaded"
                                                    : "the evidence requirements are not met"}
                                            .
                                        </>
                                    )}
                                </>
                            ) : monitoring.length === 1 ? (
                                <>
                                    Only one multi-stakeholder monitoring participation entry was
                                    recorded in {year}
                                    {monitoringWithReports > 0
                                        ? ", with a report available."
                                        : ", and no report is available."}{" "}
                                    The indicator does not score because more than one entry with
                                    attachments is required.
                                </>
                            ) : (
                                <>
                                    There is no evidence of multi-stakeholder participation in
                                    monitoring and evaluation activities in {year}. The indicator
                                    does not score.
                                </>
                            )}
                        </p>
                        <p>
                            Attached document evidence:{" "}
                            {uploadedDocumentCount === documentLinks.length && documentLinks.length > 0 ? (
                                <>
                                    All required monitoring documents (
                                    {documentLinks.map((doc) => doc.title).join("; ")}) have been
                                    uploaded to the document hub.
                                </>
                            ) : uploadedDocumentCount > 0 ? (
                                <>
                                    {uploadedDocumentCount} of {documentLinks.length} required
                                    monitoring documents have been uploaded. Missing:{" "}
                                    {documentLinks
                                        .filter((doc) => !doc.link || doc.link === "Not Uploaded")
                                        .map((doc) => doc.title)
                                        .join("; ")}
                                    .
                                </>
                            ) : (
                                <>
                                    No monitoring document evidence has been uploaded from the
                                    document hub for {year}.
                                </>
                            )}
                        </p>
                    </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
            )}
        </Comment>
    );
})

export default MonitoringProjectAndActivity;